import React, { useRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FiInfo } from "react-icons/fi";
import {
  GenericTable,
  Pagination,
  Tooltip,
  Spinner,
  FilterCard,
} from "../../Components/Common";
import * as ROUTES from "../../routes";
import { getAllTasks, getSites } from "../../Services/siteServices";
import {
  TOAST_MESSAGES,
  TASKS_LIST_PAGE_COLUMNS,
  TASKS_LIST_PAGE_LABELS,
  TASK_STATUS_MAPPING,
  TASK_STATUS_LABELS,
  MESSAGES,
} from "./Constants";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const ListTasks = () => {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    count: 0,
  });
  const [filters, setFilters] = useState({});
  const [sites, setSites] = useState([]);

  const [activeFilter, setActiveFilter] = useState(null);
  const statusRef = useRef(null);
  const siteRef = useRef(null);
  const [tempFilters, setTempFilters] = useState(filters);

  const navigate = useNavigate();

  const fetchTasks = async ({ page = 1, limit = 10, filters = {} }) => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;

      const response = await getAllTasks({
        limit,
        offset,
        ...filters,
      });

      if (response.status === 200) {
        setTasks(response.data.results);
        setPagination((prev) => ({
          ...prev,
          page,
          limit,
          count: response.data.count,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.REQUEST_ERROR;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const renderFields = (key, rowData) => {
    const value = rowData[key];

    switch (key) {
      case "status":
        return (
          <span className="capitalize font-medium text-primary">
            {TASK_STATUS_LABELS[rowData.status]}
          </span>
        );
      case "actions":
        return (
          <Tooltip text="View Task">
            <FiInfo
              className="cursor-pointer text-primary hover:text-secondary text-lg"
              onClick={() =>
                navigate(ROUTES.TASK_DETAILS.replace(":instanceId", rowData.id))
              }
            />
          </Tooltip>
        );
      default:
        if (typeof value === "object" && value !== null) {
          if (value.first_name && value.last_name) {
            return `${value.first_name} ${value.last_name}`;
          }
          return JSON.stringify(value);
        }
        return value;
    }
  };

  useEffect(() => {
    fetchTasks({
      page: pagination.page,
      limit: pagination.limit,
      filters,
    });
  }, [pagination.page, pagination.limit, filters]);

  const fetchSites = async () => {
    try {
      const response = await getSites();
      if (response.status === 200) {
        setSites(response.data.results || []);
        const formatted = (response.data.results || []).map((site) => ({
          value: site.id,
          label: site.site_name,
        }));
        setSites(formatted);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.SITES_FETCH_FAILED;
      toast.error(msg);
    }
  };

  useEffect(() => {
    fetchSites();
  }, []);

  // Close dropdown on outside click
  const handleClickOutside = (e) => {
    if (
      statusRef.current?.contains(e.target) ||
      siteRef.current?.contains(e.target)
    ) {
      return;
    }
    setActiveFilter(null);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="px-4 my-4">
      {/* Header Section */}
      <div className="grid grid-cols-1 mb-4">
        {/* Top Bar: Title + Filter Button */}
        <div className="flex items-center justify-between mb-3 mt-4 md:mt-0">
          <div className="text-xl md:text-2xl text-primary font-semibold">
            Task Overview
          </div>
        </div>

        {/* Filter Card */}
        <div>
          <FilterCard
            tempFilters={tempFilters}
            setTempFilters={setTempFilters}
            onFilterChange={(updated) => {
              setFilters(updated);
            }}
            fields={[
              {
                key: "status",
                label: "Status",
                type: "select",
                options: [...TASK_STATUS_MAPPING],
              },
              {
                key: "site",
                label: "Site",
                type: "select",
                options: [{ value: "", label: "All Sites" }, ...sites],
              },
              {
                key: "records",
                label: "Tasks",
                type: "select",
                options: [
                  { value: "", label: "All tasks" },
                  { value: "MY", label: "My tasks" },
                ],
              },
            ]}
          />
        </div>
      </div>

      {/* Table Section */}
      {loading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <Spinner />
        </div>
      ) : tasks.length > 0 ? (
        <>
          <div className="hidden md:block">
            <div className="h-[55vh] overflow-y-auto md:border md:rounded-md">
              <GenericTable
                tableColumns={TASKS_LIST_PAGE_COLUMNS || []}
                tableRows={tasks}
                columnLabels={TASKS_LIST_PAGE_LABELS}
                customError={TOAST_MESSAGES.NO_TASKS_AVAILABLE}
                renderField={renderFields}
              />
            </div>
            <Pagination
              page={pagination.page}
              totalPages={Math.max(
                1,
                Math.ceil(pagination.count / pagination.limit)
              )}
              rowsPerPage={pagination.limit}
              onPageChange={(newPage) =>
                setPagination((prev) => ({ ...prev, page: newPage }))
              }
              onPageSizeChange={(newLimit) =>
                setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }))
              }
            />
          </div>

          {/* Mobile version */}
          <div className="block md:hidden">
            <div className="h-[65vh] overflow-y-auto space-y-2">
              {tasks.map((item) => (
                <div
                  key={item.id}
                  className="p-3 border rounded-md shadow-sm hover:shadow-md transition-all cursor-pointer bg-white"
                  onClick={() =>
                    navigate(
                      ROUTES.TASK_DETAILS.replace(":instanceId", item.id)
                    )
                  }
                >
                  {/* Top Section: Site Name + Title */}
                  <div className="mb-1">
                    <div>
                      <div className="text-base font-semibold text-primary">
                        {item.site_name || " "}
                      </div>
                    </div>

                    <div className="text-sm font-medium text-gray-800">
                      {item.title || " "}
                    </div>
                  </div>

                  {/* Middle Section: Order + Weight */}
                  <div className="flex justify-start gap-16">
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-500">Order</span>
                      <span>{item.order ?? "-"}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="font-medium text-gray-500">Weight</span>
                      <span>{item.weight ?? "-"}</span>
                    </div>
                  </div>

                  {/* Bottom Section: Assigned To + Status */}
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-600 truncate max-w-[60%]">
                      {item.assigned_to
                        ? `${item.assigned_to.first_name || ""} ${
                            item.assigned_to.last_name || ""
                          }`
                        : " "}
                    </div>

                    <span
                      className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                        item.status === "COMPLETED"
                          ? "bg-green-100 text-green-700"
                          : item.status === "IN_PROGRESS"
                          ? "bg-blue-100 text-blue-700"
                          : item.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {item.status || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <Pagination
              page={pagination.page}
              totalPages={Math.ceil(pagination.count / pagination.limit)}
              rowsPerPage={pagination.limit}
              onPageChange={(newPage) =>
                setPagination((prev) => ({ ...prev, page: newPage }))
              }
              onPageSizeChange={(newLimit) =>
                setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }))
              }
            />
          </div>
        </>
      ) : (
        <div className="text-center text-gray-500 mt-6">
          {MESSAGES.NO_TASKS_AVAILABLE}
        </div>
      )}
    </div>
  );
};

export default ListTasks;
